import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Selection } from '@tiptap/pm/state';
import {
  mergeAttributes,
  Node,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';
import { m, useReducedMotion } from 'motion/react';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactElement,
} from 'react';
import { useIntl } from 'react-intl';

import { linkedContextItemKey } from '../../contracts/projects/linked-context';
import { chatObjectPreviewTarget } from '../../data/projects/artifacts/objectPreviewArtifact';
import {
  CHAT_OBJECT_TOKEN_NODE_NAME,
  chatObjectAttrsFromRecord,
  linkedContextItemForChatObject,
  type ChatObjectTokenAttrs,
} from '../../data/projects/projects-composer-editor-draft';
import { ChatTokenChip, TokenMark } from './ChatTokenChip';
import { referenceChipMessages } from './reference-chip.messages';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    chatObjectToken: {
      insertChatObjectToken: (attrs: ChatObjectTokenAttrs) => ReturnType;
    };
  }
}

// XXX: the chip tray's identity, so a profile-less patient is told apart by name, not by its empty id.
const objectKey = (attrs: ChatObjectTokenAttrs): string =>
  linkedContextItemKey(linkedContextItemForChatObject(attrs));

export const hasChatObjectToken = (
  doc: ProseMirrorNode,
  attrs: ChatObjectTokenAttrs
): boolean => {
  const key = objectKey(attrs);
  let isFound = false;
  doc.descendants((node) => {
    if (node.type.name !== CHAT_OBJECT_TOKEN_NODE_NAME) return !isFound;
    const existing = chatObjectAttrsFromRecord(node.attrs);
    isFound ||= !!existing && objectKey(existing) === key;
    return !isFound;
  });
  return isFound;
};

export interface ChatObjectTokenOptions {
  // The app wraps a token in its hover card; the package cannot resolve the object itself.
  wrapToken?: (attrs: ChatObjectTokenAttrs, token: ReactElement) => ReactElement;
  // Where a click takes the object is the host's; without it the token is not a button.
  openObject?: (attrs: ChatObjectTokenAttrs) => void;
}

export const ChatObjectToken = Node.create<ChatObjectTokenOptions>({
  name: CHAT_OBJECT_TOKEN_NODE_NAME,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { wrapToken: undefined, openObject: undefined };
  },

  addAttributes() {
    return {
      kind: {
        default: '',
        parseHTML: (element: HTMLElement) => element.dataset.objectKind ?? '',
        renderHTML: (attrs: ChatObjectTokenAttrs) => ({
          'data-object-kind': attrs.kind,
        }),
      },
      id: {
        default: '',
        parseHTML: (element: HTMLElement) => element.dataset.objectId ?? '',
        renderHTML: (attrs: ChatObjectTokenAttrs) => ({
          'data-object-id': attrs.id,
        }),
      },
      label: {
        default: '',
        parseHTML: (element: HTMLElement) => element.dataset.objectLabel ?? '',
        renderHTML: (attrs: ChatObjectTokenAttrs) => ({
          'data-object-label': attrs.label,
        }),
      },
      patientName: {
        default: undefined,
        parseHTML: (element: HTMLElement) => element.dataset.patientName || undefined,
        renderHTML: (attrs: ChatObjectTokenAttrs) =>
          attrs.patientName ? { 'data-patient-name': attrs.patientName } : {},
      },
      avatarSeed: {
        default: undefined,
        parseHTML: (element: HTMLElement) => element.dataset.avatarSeed || undefined,
        renderHTML: (attrs: ChatObjectTokenAttrs) =>
          attrs.avatarSeed ? { 'data-avatar-seed': attrs.avatarSeed } : {},
      },
      documentKind: {
        default: undefined,
        parseHTML: (element: HTMLElement) => element.dataset.documentKind || undefined,
        renderHTML: (attrs: ChatObjectTokenAttrs) =>
          attrs.documentKind ? { 'data-document-kind': attrs.documentKind } : {},
      },
      formTemplate: {
        default: undefined,
        parseHTML: (element: HTMLElement) =>
          element.dataset.formTemplate === 'true' || undefined,
        renderHTML: (attrs: ChatObjectTokenAttrs) =>
          attrs.formTemplate ? { 'data-form-template': 'true' } : {},
      },
      sessionId: {
        default: undefined,
        parseHTML: (element: HTMLElement) => element.dataset.sessionId || undefined,
        renderHTML: (attrs: ChatObjectTokenAttrs) =>
          attrs.sessionId ? { 'data-session-id': attrs.sessionId } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-chat-object-token]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-chat-object-token': '' }),
      HTMLAttributes['data-object-label'] ?? '',
    ];
  },

  addCommands() {
    return {
      insertChatObjectToken:
        (attrs) =>
        ({ state, tr }) => {
          const nodeType = state.schema.nodes[this.name];
          if (!nodeType) return false;
          // XXX: a token lands after a highlighted range, never over it; `@` mentions delete their query first.
          tr.setSelection(Selection.near(tr.doc.resolve(tr.selection.to), -1));
          tr.replaceSelectionWith(nodeType.create(attrs), false);
          tr.insertText(' ');
          return true;
        },
    };
  },

  addNodeView() {
    if (typeof ReactNodeViewRenderer !== 'function') return null;
    return ReactNodeViewRenderer(ChatObjectTokenNodeView);
  },
});

// Cancelled before ProseMirror sees it, so the editor keeps focus and its selection.
const keepEditorSelection = (event: MouseEvent) => event.preventDefault();

const passThrough = (_attrs: ChatObjectTokenAttrs, token: ReactElement) => token;

function ChatObjectTokenNodeView({
  node,
  selected,
  extension,
}: NodeViewProps): JSX.Element {
  // XXX: a node whose attrs fail validation renders empty; reading the draft
  // back drops it the same way.
  const attrs = chatObjectAttrsFromRecord(node.attrs);
  // Fade the freshly inserted token in so a citation does not pop into the
  // sentence; an opacity-only transition never affects the surrounding layout.
  const shouldReduceMotion = useReducedMotion();
  const { wrapToken, openObject } = extension.options as ChatObjectTokenOptions;
  const token = attrs ? (
    <ChatObjectTokenView
      attrs={attrs}
      selected={selected}
      onOpen={
        openObject && chatObjectPreviewTarget(attrs) ? () => openObject(attrs) : undefined
      }
    />
  ) : null;
  return (
    <NodeViewWrapper
      as="span"
      className="inline-flex align-baseline"
      contentEditable={false}
    >
      <m.span
        className="inline-flex min-w-0 items-center"
        initial={shouldReduceMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={
          shouldReduceMotion ? { duration: 0 } : { duration: 0.12, ease: 'easeOut' }
        }
      >
        {attrs && token ? (wrapToken ?? passThrough)(attrs, token) : null}
      </m.span>
    </NodeViewWrapper>
  );
}

type ChatObjectTokenViewProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  'children' | 'disabled'
> & {
  readonly attrs: ChatObjectTokenAttrs;
  readonly selected?: boolean;
  readonly disabled?: boolean;
  readonly onOpen?: () => void;
};

// Forwards its ref and trigger props so a hover card can wrap it (`asChild`).
export const ChatObjectTokenView = forwardRef<
  HTMLButtonElement,
  ChatObjectTokenViewProps
>(function ChatObjectTokenView(
  { attrs, selected = false, disabled = false, onOpen, onClick, ...rest },
  ref
) {
  const intl = useIntl();
  const label = attrs.label.trim() || attrs.kind;
  return (
    <ChatTokenChip
      {...rest}
      ref={ref}
      label={label}
      kindNoun={intl.formatMessage(referenceChipMessages[attrs.kind])}
      mark={
        <TokenMark
          kind={attrs.kind}
          label={label}
          patientName={attrs.patientName}
          avatarSeed={attrs.avatarSeed}
        />
      }
      selected={selected}
      disabled={disabled}
      dataKind={attrs.kind}
      onClick={
        onOpen
          ? (event) => {
              onClick?.(event);
              onOpen();
            }
          : undefined
      }
      onMouseDown={onOpen ? keepEditorSelection : undefined}
    />
  );
});
